import React from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";

import useAuthContext, { AuthProvider } from "./providers/authProvider";

import ActionForm from "./pages/actions/form";
import ActionsList from "./pages/actions";
import ClubHomepage from "./pages/club";
import ClubsList from "./pages/club/switch";
import CoachesPage from "./pages/coaches";
import EditCoach from "./pages/coaches/form";
import EditorSchedule from "./pages/teams/editor-schedule";
import ExerciseCollectionCollectionForm from "./pages/exercise_collections/form";
import ExerciseForm from "./pages/exercises/form";
import ExercisesList from "./pages/exercises";
import ForgotPassword from "./pages/session/forgot_password";
import GroupPracticePlaning from "./pages/practices/groupPracticePlaning";
import LessonForm from "./pages/lessons/form";
import LessonsList from "./pages/lessons";
import MembersPage from "./pages/organization/members";
import NavBar from "./components/NavBar";
import NavBarOrganization from "./components/NavBarOrganization";
import NoAccessPage from "./pages/no-access";
import OrganizationClubsList from "./pages/organization/club-switch";
import OrganizationProfile from "./pages/organization";
import OrganizationsList from "./pages/organization/organization-switch";
import PracticeEditor from "./pages/teams/practice-editor";
import PracticeTemplateForm from "./pages/practice_templates/form";
import PracticeTemplateList from "./pages/practice_templates";
import SignIn from "./pages/session/signIn";
import SubscriptionForm from "./pages/subscriptions/form";
import SubscriptionsList from "./pages/subscriptions";
import TeamDetails from "./pages/teams/form";
import TeamsList from "./pages/teams";
import VariationsList from "./pages/exercise_collections";

import { ToastContainer } from 'react-toastify';
import { RouteGuard } from "./components/RouteGuard";
import 'react-toastify/dist/ReactToastify.css'; 
import ImportAttempts from "./pages/coaches/import_attempts";


// As the router is wrapped with the provider,
// we can use our hook to check for a logged in user.
const AuthenticatedRoute = () => {
  const { user } = useAuthContext();

  if (!user) {
    return <Navigate to="/club_admin/login" replace />;
  }

  return <RouteGuard><Outlet/></RouteGuard>;
};

const UnAuthenticatedRoute = () => {
  const { user } = useAuthContext();

  if (user) {
    return <Navigate to="/club_admin/club" replace />;
  }

  return <RouteGuard><Outlet/></RouteGuard>;
};

const NavBarClubLayout = () => {
  return (
    <div className='club-admin'>
      <div className='club-admin-navbar'>
        <NavBar />
      </div>
      <div className='club-admin-content'>
        <Outlet />
      </div>
    </div>
  );
};


const NavBarOrganizationLayout = () => {
  return (
    <div className='club-admin'>
      <div className='club-admin-navbar'>
        <NavBarOrganization />
      </div>
      <div className='club-admin-content'>
        <Outlet />
      </div>
    </div>
  );
};



const Router = () => {
  return (
    <Routes>
       <Route path="/club_admin" element={<UnAuthenticatedRoute/>}>
          <Route path="/club_admin/login" element={<SignIn/>} />
          <Route path="/club_admin/forgot_password" element={<ForgotPassword/>} />
       </Route>

      <Route path="/club_admin" element={<AuthenticatedRoute/>}>
          <Route path="/club_admin/clubs" element={<ClubsList/>}/>
          <Route element={<NavBarClubLayout />}>
            <Route path="/club_admin/club" element={<ClubHomepage />}/>
            <Route path="/club_admin/" element={<Navigate to="club" replace={true} />}/>
            <Route path="/club_admin/*" element={<Navigate to="club" replace={true} />}/>
            <Route path="/club_admin/teams" element={<TeamsList />}/>
            <Route path="/club_admin/teams/:teamId" element={<TeamDetails />}/>
            <Route path="/club_admin/teams/:teamId/practice/new" element={<PracticeEditor />}/>
            <Route path="/club_admin/teams/:teamId/practice/:id" element={<PracticeEditor />}/>
            <Route path="/club_admin/teams/:teamId/schedule/new" element={<EditorSchedule />}/>
            <Route path="/club_admin/teams/:teamId/:userId" element={<EditCoach />}/>
            <Route path="/club_admin/users" element={<CoachesPage />}/>
            <Route path="/club_admin/users/:userId" element={<EditCoach />}/>
            <Route path="/club_admin/exercises" element={<ExercisesList />}/>
            <Route path="/club_admin/exercises/:id" element={<ExerciseForm />}/>
            <Route path="/club_admin/exercises/:clone_id/copy" element={<ExerciseForm />}/>
            <Route path="/club_admin/exercises/new" element={<ExerciseForm />}/>
            <Route path="/club_admin/exercise_collections" element={<VariationsList />}/>
            <Route path="/club_admin/exercise_collections/:id" element={<ExerciseCollectionCollectionForm />}/>
            <Route path="/club_admin/exercise_collections/:clone_id/copy" element={<ExerciseCollectionCollectionForm />}/>
            <Route path="/club_admin/exercise_collections/new" element={<ExerciseCollectionCollectionForm />}/>
            <Route path="/club_admin/actions" element={<ActionsList />}/>
            <Route path="/club_admin/actions/:id" element={<ActionForm />}/>
            <Route path="/club_admin/actions/new" element={<ActionForm />}/>
            <Route path="/club_admin/lessons" element={<LessonsList />}/>
            <Route path="/club_admin/lessons/:id" element={<LessonForm />}/>
            <Route path="/club_admin/lessons/new" element={<LessonForm />}/>
            <Route path="/club_admin/practice_templates" element={<PracticeTemplateList />}/>
            <Route path="/club_admin/practice_templates/:id" element={<PracticeTemplateForm />}/>
            <Route path="/club_admin/practice_templates/:clone_id/copy" element={<PracticeTemplateForm />}/>
            <Route path="/club_admin/practice_templates/new" element={<PracticeTemplateForm />}/>
            <Route path="/club_admin/group_practices/:method" element={<GroupPracticePlaning />}/>
            <Route path="/club_admin/subscriptions" element={<SubscriptionsList />}/>
            <Route path="/club_admin/subscriptions/:id" element={<SubscriptionForm />}/>
            <Route path="/club_admin/import_attempts/:id" element={<ImportAttempts />}/>
          </Route>

          <Route element={<NavBarOrganizationLayout />}>
          <Route path="/club_admin/organization" element={<OrganizationProfile />}/>
          <Route path="/club_admin/clubs_organization" element={<OrganizationClubsList/>}/>
          <Route path="/club_admin/members" element={<MembersPage/>}/>
          </Route>
          <Route path="/club_admin/organizations" element={<OrganizationsList/>}/>

        <Route path="/club_admin/no_access" element={<NoAccessPage/>}/>
      </Route>
    </Routes>
  );
};

export default () => {
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <AuthProvider>
          <ToastContainer position="top-right" autoClose={5000} hideProgressBar />
          <Router/>
        </AuthProvider>
      </BrowserRouter>
    </I18nextProvider>
  );
}
